import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Heart, Envelope as Mail } from '@phosphor-icons/react';
import { supabase } from '@/integrations/supabase/client';

interface SavedFavoritesDialogProps {
  favoriteIds: string[];
}

export const SavedFavoritesDialog = ({ favoriteIds }: SavedFavoritesDialogProps) => {
  const [email, setEmail] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const handleSendFavorites = async () => {
    if (!email || favoriteIds.length === 0) {
      toast({
        title: "Eroare",
        description: "Introduceți adresa de email și asigurați-vă că aveți favorite salvate.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-favorites-email', {
        body: { email, listingIds: favoriteIds }
      });

      if (error) throw error;

      toast({
        title: "Email trimis! 📧",
        description: `Am trimis ${favoriteIds.length} favorite la ${email}`,
      });
      setIsOpen(false);
      setEmail('');
    } catch (error: any) {
      console.error('Error sending favorites:', error);
      toast({
        title: "Eroare la trimitere",
        description: error.message || "Nu am putut trimite email-ul. Încercați din nou.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="gap-2"
          disabled={favoriteIds.length === 0}
        >
          <Mail className="h-4 w-4" />
          Trimite pe Email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500 fill-current" />
            Trimite Favoritele Tale
          </DialogTitle>
          <DialogDescription>
            Primești pe email {favoriteIds.length} anunț{favoriteIds.length !== 1 ? 'uri' : ''} salvat{favoriteIds.length !== 1 ? 'e' : ''}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="email">Adresa de Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="exemplu@email.ro"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isSending) {
                  handleSendFavorites();
                }
              }}
            />
          </div>
          <Button 
            onClick={handleSendFavorites} 
            disabled={isSending || !email}
            className="w-full"
          >
            {isSending ? 'Se trimite...' : 'Trimite Favoritele'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
